import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

export type GenieDocumentTemplateKey = "faq" | "how-to" | "one-pager";

/**
 * The `documents.source_type` every branded document is saved under.
 *
 * It is the line between what a Genie *learned* and what a Genie *produced*.
 * The knowledge page filters these out of the source library, the brain, and the
 * summariser, so a generated document can never be mistaken for a source or be
 * fed back in as one.
 */
export const GENIE_DOCUMENT_SOURCE_TYPE = "genie-document";

export interface GenieDocumentSection {
  heading: string;
  body: string;
}

export interface GeneratedGenieDocument {
  title: string;
  subtitle?: string;
  template: GenieDocumentTemplateKey;
  sections: GenieDocumentSection[];
}

export interface GenerateGenieDocumentInput {
  agentId: string;
  template: GenieDocumentTemplateKey;
  purpose?: string;
  documentIds: string[];
  genieName?: string;
}

export interface GenerateGenieDocumentResult {
  document: GeneratedGenieDocument;
  meta: {
    sourceDocuments: string[];
    skippedDocuments: string[];
    truncated: boolean;
  };
}

/** Co-branding used on the generated document + PDF (customer brand leads). */
export interface GenieDocumentBrand {
  name: string;
  tagline?: string | null;
  logoUrl?: string | null;
  primaryColor: string;
  secondaryColor?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Saving — a branded document is a `documents` row, not a downloaded file
// ─────────────────────────────────────────────────────────────────────────────

export interface SaveGenieDocumentInput {
  /** Omit to create; pass the document id to update in place. */
  id?: string;
  agentId: string;
  title: string;
  subtitle?: string;
  template: GenieDocumentTemplateKey;
  sections: GenieDocumentSection[];
  /** Free-text focus the document was generated against, kept for regeneration. */
  purpose?: string;
  /** Knowledge sources it was built from, kept so we can show its provenance. */
  sourceDocumentIds?: string[];
  /** Format key from `GENIE_DOCUMENT_FORMATS`, kept for the library's icon/label. */
  formatKey?: string;
}

/**
 * The structured document as stored on `documents.metadata.genie_document`.
 * Everything needed to re-open the editor without re-parsing the markdown.
 */
export interface StoredGenieDocument extends GeneratedGenieDocument {
  purpose?: string;
  formatKey?: string;
  sourceDocumentIds?: string[];
  agentId?: string;
  generatedAt?: string;
}

export interface SaveGenieDocumentResult {
  document: DocumentWithMetadata;
  created: boolean;
  /** Set on creates only: whether the follow-up auto-publish landed. */
  published?: boolean;
}

/**
 * Reads the structured document back off a saved row.
 * Returns null for any document that is not one of ours, so callers can filter
 * a mixed document list with a single pass.
 */
export const readStoredGenieDocument = (
  doc: DocumentWithMetadata | null | undefined,
): StoredGenieDocument | null => {
  if (!doc || doc.source_type !== GENIE_DOCUMENT_SOURCE_TYPE) return null;

  const stored = doc.metadata?.genie_document as StoredGenieDocument | undefined;
  if (!stored || !Array.isArray(stored.sections)) return null;

  return stored;
};

/** True when this row is a branded document rather than a knowledge source. */
export const isGenieDocument = (doc: DocumentWithMetadata): boolean =>
  doc.source_type === GENIE_DOCUMENT_SOURCE_TYPE;

// ─────────────────────────────────────────────────────────────────────────────
// Formats — the menu shown to the owner
// ─────────────────────────────────────────────────────────────────────────────

export interface GenieDocumentTemplateOption {
  key: GenieDocumentTemplateKey;
  label: string;
  description: string;
  genieIcon: GenieIconName;
}

/** The three shapes the generator itself can produce. */
export const GENIE_DOCUMENT_TEMPLATES: GenieDocumentTemplateOption[] = [
  {
    key: "faq",
    label: "FAQ Sheet",
    description: "The questions your customers ask most, answered clearly.",
    genieIcon: "chat-check",
  },
  {
    key: "how-to",
    label: "How-To Guide",
    description: "A step-by-step guide that walks people through a process.",
    genieIcon: "clipboard-check",
  },
  {
    key: "one-pager",
    label: "One-Pager",
    description: "A concise overview of what you offer, on a single page.",
    genieIcon: "document",
  },
];

/**
 * Formats offered in the Documents area.
 *
 * `available` formats route through the existing generator and its branded PDF
 * renderer — there is no second pipeline, only a seeded focus. `planned` ones
 * are shown and not offered: the shape is settled but the generator template
 * does not exist yet.
 */
export interface GenieDocumentFormat {
  key: string;
  label: string;
  description: string;
  genieIcon: GenieIconName;
  status: "available" | "planned";
  /** Set for `available` formats — the generator template this maps onto. */
  template?: GenieDocumentTemplateKey;
  /** Seed focus passed to the generator. */
  purpose?: string;
}

export const GENIE_DOCUMENT_FORMATS: GenieDocumentFormat[] = [
  {
    key: "faq-sheet",
    label: "FAQ sheet",
    description: "The questions customers ask most, answered on one page.",
    genieIcon: "chat-check",
    status: "available",
    template: "faq",
  },
  {
    key: "how-to-guide",
    label: "How-to guide",
    description: "A step-by-step walkthrough of one process.",
    genieIcon: "clipboard-check",
    status: "available",
    template: "how-to",
  },
  {
    key: "one-pager",
    label: "One-pager",
    description: "A concise overview of what you offer, on a single page.",
    genieIcon: "document",
    status: "available",
    template: "one-pager",
  },
  {
    key: "staff-answer-sheet",
    label: "Staff answer sheet",
    description: "What to say when someone asks — a crib sheet for the counter.",
    genieIcon: "chat",
    status: "available",
    template: "how-to",
    purpose: "A quick-reference answer sheet for staff, written for someone under pressure.",
  },
  {
    key: "policy-one-pager",
    label: "Policy one-pager",
    description: "A single page stating a policy plainly.",
    genieIcon: "files",
    status: "available",
    template: "one-pager",
    purpose: "A plain-language policy summary a customer can read in under a minute.",
  },
  {
    key: "price-list",
    label: "Price list",
    description: "Prices and inclusions, laid out for the wall.",
    genieIcon: "dashboard",
    status: "planned",
  },
  {
    key: "quarterly-report",
    label: "Quarterly report",
    description: "What changed this quarter, drawn from your knowledge and calls.",
    genieIcon: "brain",
    status: "planned",
  },
];

export const findGenieDocumentFormat = (key?: string | null): GenieDocumentFormat | undefined =>
  key ? GENIE_DOCUMENT_FORMATS.find((format) => format.key === key) : undefined;

// ─────────────────────────────────────────────────────────────────────────────
// Document suite — the swarm: one planner proposes, parallel drafters write
// ─────────────────────────────────────────────────────────────────────────────

/** One proposed document in a suite, as returned by `propose-suite`. */
export interface GenieDocumentSuiteProposal {
  title: string;
  template: GenieDocumentTemplateKey;
  /** One line on why it's worth making — shown on the review checklist. */
  why: string;
  /** Knowledge sources it should draw from (ids into the genie's sources). */
  documentIds: string[];
}

export interface ProposeGenieDocumentSuiteResult {
  suite: GenieDocumentSuiteProposal[];
  meta: {
    sourceDocuments: string[];
    truncated: boolean;
  };
}

/** One document the owner approved for generation. */
export interface GenerateGenieDocumentSuiteInput {
  agentId: string;
  suite: Array<{
    title: string;
    template: GenieDocumentTemplateKey;
    documentIds?: string[];
  }>;
}

export interface GenerateGenieDocumentSuiteResult {
  created: Array<{
    id: string;
    title: string;
    template: GenieDocumentTemplateKey;
    /** Judge verdict: false means the draft may contain an invented fact. */
    grounded: boolean;
    concern?: string;
  }>;
  failed: Array<{ title: string; reason: string }>;
}

/** Falls back to the template's own icon/label when a row predates format keys. */
export const genieDocumentFormatFor = (
  stored: StoredGenieDocument | null,
): GenieDocumentFormat | GenieDocumentTemplateOption | undefined => {
  if (!stored) return undefined;
  return (
    findGenieDocumentFormat(stored.formatKey) ??
    GENIE_DOCUMENT_FORMATS.find((format) => format.template === stored.template) ??
    GENIE_DOCUMENT_TEMPLATES.find((template) => template.key === stored.template)
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page content ideas — recommend-page-content drafts the page's manual slices
// ─────────────────────────────────────────────────────────────────────────────

/** One hero copy variant for the Genie Page, drafted from the genie's sources. */
export interface GeniePageHeroIdea {
  headline: string;
  subheadline: string;
  askPlaceholder: string;
}

/**
 * One owner-written section drafted from the genie's sources. Same shape as
 * `HubCustomSection` minus the ids — the editor mints those when the owner
 * applies the idea.
 */
export interface GeniePageSectionIdea {
  title: string;
  intro: string | null;
  fields: Array<{ label: string; value: string }>;
}

/** Contact details the sources state — null when they don't. */
export interface GeniePageContactIdea {
  hours: string | null;
  address: string | null;
}

export interface RecommendGeniePageContentResult {
  heroes: GeniePageHeroIdea[];
  sections: GeniePageSectionIdea[];
  contact: GeniePageContactIdea | null;
}

/**
 * What the one-command page creation actually did — shown on the command card
 * afterwards so the owner can see what was drafted, what was skipped (already
 * there), and what needs a look before it goes live.
 */
export interface CreateGeniePageSummary {
  heroApplied: boolean;
  sectionsAdded: number;
  contactApplied: boolean;
  questionsFeatured: number;
  guidesCreated: number;
  guidesPublished: number;
  /** Drafted guides the fact-check judge flagged — created, but left unpublished. */
  guidesNeedingReview: string[];
  /** Human-readable skips, e.g. "Hero already written" — owner work is never overwritten. */
  skipped: string[];
}
